import Vue from 'vue'
import apiClient from '@/services/axios'
import { deviceMetadataComboKey, deviceMetadataValidComboSet } from '@/utils/deviceMetadata'
import { normalizeDateInput } from '@/utils/dateInput'

export async function createDevice(payload) {
  return apiClient.post('/devices', payload)
}

export async function createRFID(payload) {
  return apiClient.post('/devices/rfid', payload)
}

export async function getRFIDs(filter) {
  return apiClient.post('/devices/searchRFIDs', filter)
}

export async function getRFIDByID(id) {
  return apiClient.get(`/devices/rfid/${id}`)
}

export async function deleteRFID(id) {
  return apiClient.delete(`/devices/rfid/${id}`)
}

export async function updateRFID(id, payload) {
  return apiClient.patch(`/devices/rfid/${id}`, payload)
}

export async function createBadgeHolder(payload) {
  return apiClient.post('/devices/badge-holder', payload)
}

export async function getBadgeHolders(filter) {
  return apiClient.post('/devices/searchBadgeHolders', filter)
}

export async function getBadgeHolderByID(id) {
  return apiClient.get(`/devices/badge-holder/${id}`)
}

export async function deleteBadgeHolder(id) {
  return apiClient.delete(`/devices/badge-holder/${id}`)
}

export async function updateBadgeHolder(id, payload) {
  return apiClient.patch(`/devices/badge-holder/${id}`, payload)
}

export async function getDevices(filter) {
  return apiClient.post('/devices/searchDevices', filter)
}

export async function getDeviceByID(id) {
  try {
    const response = await apiClient.get(`/devices/${id}`)
    if (response.data) {
      return {
        success: true,
        status: 200,
        message: 'Device data',
        data: response.data,
      }
    } else {
      return {
        success: false,
        status: 404,
        data: {},
        message: `Device with id: ${id} not found!`,
      }
    }
  } catch (error) {
    return {
      success: false,
      status: 500,
      message: 'Error fetching device by ID',
      error,
    }
  }
}

export async function listAllDevices() {
  return apiClient.get('/devices')
}

export async function getDeviceMetadata() {
  return apiClient.get('/device-metadata')
}

export async function updateDevice(id, deviceData) {
  try {
    const response = await apiClient.patch(`/devices/${id}`, deviceData)
    return {
      status: response.status,
      message: 'Device updated successfully',
      data: response.data,
    }
  } catch (error) {
    console.error('Error updating device:', error)
    return {
      status: error.response ? error.response.status : 500,
      message: 'Error updating device',
      error: error.response ? error.response.data : error.message,
    }
  }
}

export async function deleteDevice(id) {
  return apiClient.delete(`/devices/${id}`)
}

export async function deleteDevices(ids) {
  return apiClient.delete('/devices', { data: { ids } })
}

export async function uploadExistingDeviceData(data) {
  const metaResponse = await getDeviceMetadata()
  const metadataRows = metaResponse?.data
  if (!Array.isArray(metadataRows) || metadataRows.length === 0) {
    Vue.prototype.$notification.warning({
      message:
        'Device metadata is missing or empty. Add rows to the device_metadata table first.',
    })
    return
  }
  const allowedCombos = deviceMetadataValidComboSet(metadataRows)

  const dResponse = await listAllDevices()
  const allDevices = dResponse.data
  for (let i = 0; i < data.length; i++) {
    const element = data[i]
    if (!element.deviceType) {
      Vue.prototype.$notification.warning({
        message: `Row ${i + 1} Device Type is empty`,
      })
      return
    }
    if (!element.deviceMake) {
      Vue.prototype.$notification.warning({
        message: `Row ${i + 1} Device Make is empty`,
      })
      return
    }
    if (!element.deviceModel) {
      Vue.prototype.$notification.warning({
        message: `Row ${i + 1} Device Model is empty`,
      })
      return
    }
    if (!element.deviceSerialNumber) {
      Vue.prototype.$notification.warning({
        message: `Row ${i + 1} Device Serial Number is empty`,
      })
      return
    }
    if (!element.deviceIMEINumber) {
      Vue.prototype.$notification.warning({
        message: `Row ${i + 1} Device IMEI Number is empty`,
      })
      return
    }
    const combo = deviceMetadataComboKey(
      element.deviceType,
      element.deviceMake,
      element.deviceModel,
    )
    if (!allowedCombos.has(combo)) {
      Vue.prototype.$notification.warning({
        message: `Row ${i + 1} Device Type / Make / Model is not a valid combination`,
      })
      return
    }
    const device = allDevices.filter(
      (device) =>
        device.imeiNumber === element.deviceIMEINumber.toString().trim(),
    )
    if (device.length > 0) {
      Vue.prototype.$notification.warning({
        message: `Row ${i + 1} Device IMEI Number is already exist`,
      })
      return
    }
  }

  try {
    let failed = []
    let success = []
    let exists = []
    let response
    for (let v = 0; v < data.length; v++) {
      const element = data[v]
      if (
        element.vehicleIdentifier &&
        element.licensePlate &&
        element.vinNumber
      ) {
        const vData = {
          vehicleId: element.vehicleIdentifier,
          vinNumber: element.vinNumber,
          licensePlate: element.licensePlate,
          // The sheet is read raw, so date cells arrive as Excel serials
          // (45483); stored as-is they rendered as "01/01/45483".
          installDate: normalizeDateInput(element.deviceInstallDate),
          startDate: normalizeDateInput(element.deviceStartDate),
          type: element.deviceType,
          make: element.deviceMake,
          model: element.deviceModel,
          serialNumber: element.deviceSerialNumber,
          imeiNumber: element.deviceIMEINumber,
          notes: element.deviceNotes,
        }
        if (element.vehicleIdentifier) {
          try {
            await createDevice(vData)
            success.push(element)
          } catch (error) {
            // A 409 (IMEI already registered, or the vehicle already has this
            // model) skips the row instead of aborting the whole upload.
            const reason =
              (error.response && error.response.data && error.response.data.message) ||
              error.message
            failed.push({ ...element, reason })
            console.error(`Device upload row ${v + 1} failed:`, reason)
          }
        }

        if (v === data.length - 1) {
          return (response = {
            status: 200,
            message: 'Device data uploaded successfully',
            failed,
            success,
            exists,
          })
        }
      }
    }
  } catch (error) {
    return { status: 500, message: ':::: UploadExistingDeviceData :::', error }
  }
}
